import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  ArrowUpDown, CalendarIcon, Download, Phone, PhoneCall, Timer, Trophy, BellRing, ChevronDown,
} from "lucide-react";
import { format, subDays } from "date-fns";
import { toast } from "sonner";

const OUTCOME_META = [
  { key: "connected", label: "Connected", color: "#059669" },
  { key: "callback_requested", label: "Callback", color: "#D97706" },
  { key: "busy", label: "Busy", color: "#EA580C" },
  { key: "switched_off", label: "Switched off", color: "#64748B" },
  { key: "not_interested", label: "Not interested", color: "#E11D48" },
  { key: "other", label: "Other", color: "#CBD5E1" },
];

const PRESETS = [
  { id: "today", label: "Today", days: 0 },
  { id: "7d", label: "Last 7 days", days: 6 },
  { id: "30d", label: "Last 30 days", days: 29 },
  { id: "custom", label: "Custom" },
];

const today = () => format(new Date(), "yyyy-MM-dd");

function DateField({ value, onChange, testid, placeholder }) {
  const date = value ? new Date(value + "T00:00:00") : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testid} className="w-[150px] h-9 text-xs justify-start font-normal bg-white">
          <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
          {date ? format(date, "dd MMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
}

function fmtMins(m) {
  if (m == null) return "—";
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  return r ? `${h}h ${r}m` : `${h}h`;
}

function OutcomeBar({ breakdown, total, height = "h-2.5" }) {
  if (!total) return <div className={`w-full ${height} rounded-full bg-slate-100`} />;
  return (
    <div className={`w-full ${height} rounded-full overflow-hidden flex bg-slate-100`}>
      {OUTCOME_META.map((o) => {
        const v = breakdown?.[o.key] || 0;
        if (!v) return null;
        return <div key={o.key} style={{ width: `${(v / total) * 100}%`, background: o.color }} title={`${o.label}: ${v}`} />;
      })}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="font-mono text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
}

export default function CounselorReports() {
  const [preset, setPreset] = useState("30d");
  const [range, setRange] = useState(() => ({ from: format(subDays(new Date(), 29), "yyyy-MM-dd"), to: today() }));
  const [interval, setIntervalUnit] = useState("day");
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ by: "fees_paid", dir: "desc" });
  const [expanded, setExpanded] = useState(null);

  const applyPreset = (id) => {
    setPreset(id);
    if (id === "custom") return;
    const days = PRESETS.find((p) => p.id === id).days;
    setRange({ from: format(subDays(new Date(), days), "yyyy-MM-dd"), to: today() });
  };

  const params = useMemo(() => ({ from_date: range.from, to_date: range.to }), [range]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/analytics/counselors/performance", { params }),
      api.get("/analytics/counselors/calling-timeline", { params: { ...params, interval } }),
    ])
      .then(([p, t]) => { setData(p.data); setTimeline(t.data.buckets || []); })
      .catch(() => toast.error("Could not load counselor reports"))
      .finally(() => setLoading(false));
  }, [params, interval]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/analytics/counselors/export", { params, responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `counselor_performance_${range.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const rows = useMemo(() => {
    const list = [...(data?.counselors || [])];
    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sort.by === "calls_logged") return (a.calls_logged - b.calls_logged) * dir;
      if (sort.by === "conversion_rate") return (a.conversion_rate - b.conversion_rate) * dir;
      return (a.fees_paid - b.fees_paid) * dir || (a.calls_logged - b.calls_logged) * dir;
    });
    return list;
  }, [data, sort]);

  const totals = data?.totals || {};
  const outcomeTotal = OUTCOME_META.reduce((s, o) => s + (totals.call_outcomes_breakdown?.[o.key] || 0), 0);
  const maxCalls = Math.max(1, ...timeline.map((b) => b.calls));

  const toggleSort = (by) => setSort((s) => (s.by === by ? { by, dir: s.dir === "asc" ? "desc" : "asc" } : { by, dir: "desc" }));

  return (
    <div data-testid="counselor-reports-page" className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Counselor Performance & Calling Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Tele-caller productivity, call outcomes, response times and fee conversion.</p>
        </div>
        <Button className="bg-brand w-full lg:w-auto" data-testid="counselor-export-csv" onClick={exportCsv} disabled={exporting || loading}>
          <Download className="w-4 h-4 mr-1.5" /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      {/* filter bar */}
      <Card className="p-3 border-slate-200" data-testid="counselor-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <Button key={p.id} size="sm" variant={preset === p.id ? "default" : "outline"}
              data-testid={`counselor-preset-${p.id}`}
              className={preset === p.id ? "bg-brand h-8" : "h-8"} onClick={() => applyPreset(p.id)}>
              {p.label}
            </Button>
          ))}
          {preset === "custom" && (
            <>
              <DateField testid="counselor-date-from" value={range.from} placeholder="From"
                onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
              <DateField testid="counselor-date-to" value={range.to} placeholder="To"
                onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
            </>
          )}
          <span className="mx-1 h-5 w-px bg-slate-200" />
          <span className="text-xs text-slate-500">Trend:</span>
          {["day", "week"].map((u) => (
            <Button key={u} size="sm" variant={interval === u ? "default" : "outline"}
              data-testid={`counselor-interval-${u}`}
              className={interval === u ? "bg-brand h-8 capitalize" : "h-8 capitalize"} onClick={() => setIntervalUnit(u)}>
              {u}
            </Button>
          ))}
        </div>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="counselor-kpi-cards">
        <Kpi icon={PhoneCall} label="Total Calls Logged" testid="kpi-calls"
          value={(totals.calls_logged ?? 0).toLocaleString("en-IN")}
          sub={`${totals.connected_rate ?? 0}% connected · ${(totals.connected_calls ?? 0).toLocaleString("en-IN")} calls`} />
        <Kpi icon={Timer} label="Avg Response Time" testid="kpi-response"
          value={fmtMins(totals.first_response_time_avg)}
          sub="lead created → first counselor touch" />
        <Kpi icon={Trophy} label="Top Performer" testid="kpi-top"
          value={totals.top_counselor ? totals.top_counselor.name.split(" ")[0] : "—"}
          sub={totals.top_counselor ? `${totals.top_counselor.fees_paid} paid · ${totals.top_counselor.conversion_rate}% conv.` : "no fee conversions yet"} />
        <Kpi icon={BellRing} label="Follow-ups Pending" testid="kpi-followups"
          value={(totals.followups_pending ?? 0).toLocaleString("en-IN")}
          sub="callbacks with no follow-up logged" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* outcome distribution */}
        <Card className="p-5 border-slate-200" data-testid="counselor-outcome-distribution">
          <h2 className="font-semibold text-slate-800 mb-4">Call Outcome Distribution</h2>
          <OutcomeBar breakdown={totals.call_outcomes_breakdown} total={outcomeTotal} height="h-3" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-4">
            {OUTCOME_META.map((o) => {
              const v = totals.call_outcomes_breakdown?.[o.key] || 0;
              const pct = outcomeTotal ? Math.round((v / outcomeTotal) * 100) : 0;
              return (
                <div key={o.key} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: o.color }} />
                  <span className="text-slate-600">{o.label}</span>
                  <span className="ml-auto font-mono text-slate-900">{v}</span>
                  <span className="font-mono text-slate-400 w-9 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* calling timeline */}
        <Card className="p-5 border-slate-200" data-testid="counselor-calling-timeline">
          <h2 className="font-semibold text-slate-800 mb-4">Calls Logged ({interval === "week" ? "weekly" : "daily"})</h2>
          {timeline.length === 0 ? (
            <div className="text-sm text-slate-400 py-10 text-center">No calls in this range.</div>
          ) : (
            <div className="flex items-end gap-0.5 h-40 overflow-x-auto">
              {timeline.map((b) => (
                <div key={b.date} className="flex-1 min-w-[6px] flex flex-col justify-end group relative" title={`${b.date}: ${b.calls} calls (${b.connected} connected)`}>
                  <div className="w-full rounded-t-sm bg-slate-200" style={{ height: `${(b.calls / maxCalls) * 100}%` }}>
                    <div className="w-full rounded-t-sm bg-emerald-500" style={{ height: b.calls ? `${(b.connected / b.calls) * 100}%` : 0 }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Connected</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-200" /> Other</span>
          </div>
        </Card>
      </div>

      {/* leaderboard */}
      <Card className="border-slate-200" data-testid="counselor-leaderboard">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Productivity Leaderboard</h2>
          <span className="text-xs text-slate-400">{data?.counselors?.length || 0} counselors</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">#</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Counselor</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned</TableHead>
                <TableHead>
                  <button data-testid="sort-calls" onClick={() => toggleSort("calls_logged")}
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900">
                    Calls Made <ArrowUpDown className={`w-3 h-3 ${sort.by === "calls_logged" ? "text-blue-600" : "text-slate-300"}`} />
                  </button>
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[120px]">Outcome mix</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paid</TableHead>
                <TableHead>
                  <button data-testid="sort-conversion" onClick={() => toggleSort("conversion_rate")}
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900">
                    Conv % <ArrowUpDown className={`w-3 h-3 ${sort.by === "conversion_rate" ? "text-blue-600" : "text-slate-300"}`} />
                  </button>
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">1st response</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400" data-testid="counselor-empty">No counselors for this tenant.</TableCell></TableRow>
              )}
              {!loading && rows.map((c, i) => {
                const callTotal = OUTCOME_META.reduce((s, o) => s + (c.call_outcomes_breakdown?.[o.key] || 0), 0);
                const open = expanded === c.counselor_id;
                return (
                  <Fragment key={c.counselor_id}>
                    <TableRow data-testid={`counselor-row-${i}`} className="hover:bg-slate-50/60">
                      <TableCell className="text-slate-400 font-mono text-xs">{i + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2.5 min-w-[160px]">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs bg-slate-100 text-slate-600">{initials(c.name)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                            <div className="text-xs text-slate-400 truncate">{c.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{c.total_assigned_leads}</TableCell>
                      <TableCell className="font-mono">{c.calls_logged}</TableCell>
                      <TableCell><OutcomeBar breakdown={c.call_outcomes_breakdown} total={callTotal} /></TableCell>
                      <TableCell className="font-mono text-teal-700">{c.fees_paid}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(c.conversion_rate, 100)}%` }} />
                          </div>
                          <span className="font-mono text-xs">{c.conversion_rate}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{fmtMins(c.first_response_time_avg)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" data-testid={`counselor-details-${i}`}
                          onClick={() => setExpanded(open ? null : c.counselor_id)}>
                          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow data-testid={`counselor-detail-${i}`} className="bg-slate-50/60">
                        <TableCell colSpan={9}>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 py-2 text-sm">
                            <div>
                              <div className="text-xs text-slate-500 mb-1">Connected calls</div>
                              <div className="font-mono text-slate-900">{c.connected_calls} <span className="text-slate-400">/ {c.calls_logged}</span></div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 mb-1">Callbacks requested</div>
                              <div className="font-mono text-slate-900">{c.callbacks_requested}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 mb-1">Unreachable / busy</div>
                              <div className="font-mono text-slate-900">{c.unreachable_calls}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 mb-1">Follow-ups pending</div>
                              <div className="font-mono text-slate-900">{c.followups_pending}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 mb-1">Applications started</div>
                              <div className="font-mono text-slate-900">{c.applications_started}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 mb-1">Fees paid</div>
                              <div className="font-mono text-slate-900">{c.fees_paid}</div>
                            </div>
                            <div className="sm:col-span-2 lg:col-span-2">
                              <div className="text-xs text-slate-500 mb-1">Outcome breakdown</div>
                              <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-xs">
                                {OUTCOME_META.map((o) => (
                                  <span key={o.key} className="text-slate-600">
                                    <span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: o.color }} />
                                    {o.label}: {c.call_outcomes_breakdown?.[o.key] || 0}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
